import React from 'react';
import dayjs from 'dayjs';
import { cn } from '@frontend/shadcn/lib/utils';
import { Tooltip, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { PercentChangeBadge } from './percent-change-badge';
import { TooltipContent, TooltipArrow } from './tooltip';
import { DateType } from '../types';

interface MetricChangeIndicatorProps {
  current: number;
  previous: number;
  startDate?: string;
  endDate?: string;
  dateType?: DateType;
  className?: string;
  showTimeRange?: boolean;
  timeRangePrefix?: string;
  customTimeRangeText?: string;
  showComparisonTooltip?: boolean;
  formatType?: 'currency' | 'number';
}

// Helper function to calculate percentage change
const calculatePercentageChange = (current: number, previous: number): number => {
  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }
  return ((current - previous) / previous) * 100;
};

// Helper function to get friendly date text for time ranges
const getFriendlyDateText = (startDate?: string, endDate?: string, dateType?: DateType): string => {
  // Handle predefined date types with friendly names
  if (dateType) {
    switch (dateType) {
      case 'thisWeek':
        return 'this week';
      case 'lastWeek':
        return 'last week';
      case 'monthToDate':
        return 'month to date';
      case 'lastMonth':
        return 'last month';
      case 'quarterToDate':
        return 'quarter to date';
      case 'lastQuarter':
        return 'last quarter';
      case 'last6Months':
        return 'last 6 months';
      case 'yearToDate':
        return 'year to date';
      case 'allTime':
        return 'all time';
      default:
        break;
    }
  }

  // For custom ranges or when dateType is not provided, display the actual date range
  if (!startDate || !endDate) {
    return 'selected period';
  }

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  // Check if it's the same day
  if (start.isSame(end, 'day')) {
    return start.format('MMM D, YYYY');
  }

  // Always show both dates with year for clarity
  return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
};

// Helper function to calculate previous period dates
const getPreviousPeriodDates = (startDate?: string, endDate?: string, _dateType?: DateType): { start: string; end: string } | null => {
  if (!startDate || !endDate) {
    return null;
  }

  const currentStart = dayjs(startDate);
  const currentEnd = dayjs(endDate);

  // Validate dates
  if (!currentStart.isValid() || !currentEnd.isValid()) {
    return null;
  }

  // Ensure start is before end - if not, swap them
  const actualStart = currentStart.isAfter(currentEnd) ? currentEnd : currentStart;
  const actualEnd = currentStart.isAfter(currentEnd) ? currentStart : currentEnd;

  // Calculate the exact number of days in the current period
  const periodDays = actualEnd.diff(actualStart, 'day') + 1;

  // For previous period: go back the same number of days
  // Previous period ends the day before current period starts
  const previousEnd = actualStart.subtract(1, 'day');
  // Previous period starts by subtracting the period length from previous end
  const previousStart = previousEnd.subtract(periodDays - 1, 'day');

  return {
    start: previousStart.format('YYYY-MM-DD'),
    end: previousEnd.format('YYYY-MM-DD')
  };
};

// Helper function to get tooltip text for previous period
const getPreviousPeriodTooltipText = (startDate?: string, endDate?: string, dateType?: DateType, current?: number, previous?: number, formatType?: 'currency' | 'number'): string => {
  const previousPeriodDates = getPreviousPeriodDates(startDate, endDate, dateType);

  if (!previousPeriodDates) {
    return 'Compared to the previous period.';
  }

  const percentageChange = current !== undefined && previous !== undefined
    ? calculatePercentageChange(current, previous)
    : null;

  // Format the previous value with conditional currency formatting
  const formatValue = (value: number, isCurrency: boolean = false): string => {
    if (value >= 1000000) {
      const formatted = (value / 1000000).toFixed(1);
      return isCurrency ? `$${formatted}M` : `${formatted}M`;
    } else if (value >= 1000) {
      const formatted = (value / 1000).toFixed(1);
      return isCurrency ? `$${formatted}K` : `${formatted}K`;
    } else {
      const formatted = value.toLocaleString();
      return isCurrency ? `$${formatted}` : formatted;
    }
  };

  // Get percentage change direction
  const getChangeDirection = (change: number): string => {
    if (change > 0) return 'increase';
    if (change < 0) return 'decrease';
    return 'no change';
  };

  const previousPeriodText = getFriendlyDateText(previousPeriodDates.start, previousPeriodDates.end, undefined);
  const formattedPreviousValue = previous !== undefined ? formatValue(previous, formatType === 'currency') : '';
  const changeDirection = percentageChange !== null ? getChangeDirection(percentageChange) : '';
  const formattedPercentage = percentageChange !== null ? `${Math.abs(Math.round(percentageChange))}%` : '';

  // If we have a predefined dateType, use it for context
  if (dateType) {
    const dateTypeLabels: Record<DateType, string> = {
      thisWeek: 'week',
      lastWeek: 'week',
      monthToDate: 'month',
      lastMonth: 'month',
      quarterToDate: 'quarter',
      lastQuarter: 'quarter',
      last6Months: '6-month period',
      yearToDate: 'year',
      allTime: 'period',
      custom: 'period'
    };

    const periodLabel = dateTypeLabels[dateType] || 'period';

    if (percentageChange !== null && previous !== undefined) {
      return `Previous ${periodLabel}: ${formattedPreviousValue} (${previousPeriodText}) - ${formattedPercentage} ${changeDirection}.`;
    }

    return `Compared to the previous ${periodLabel} (${previousPeriodText}).`;
  }

  // For custom date ranges, show the previous period dates
  if (percentageChange !== null && previous !== undefined) {
    return `Previous period: ${formattedPreviousValue} (${previousPeriodText}) - ${formattedPercentage} ${changeDirection}.`;
  }

  return `Compared to the previous period (${previousPeriodText}).`;
};

export const MetricChangeIndicator: React.FC<MetricChangeIndicatorProps> = ({
  current,
  previous,
  startDate,
  endDate,
  dateType,
  className = '',
  showTimeRange = true,
  timeRangePrefix = 'in',
  customTimeRangeText,
  showComparisonTooltip = false,
  formatType,
}) => {
  // Calculate percentage change
  const growthPercentage = calculatePercentageChange(current, previous);

  // Get friendly date text if showing time range
  const timeRange = showTimeRange ? customTimeRangeText || getFriendlyDateText(startDate, endDate, dateType) : '';

  // Rendered indicator element (percent badge + optional time range)
  const indicator = (
    <div className={cn('text-sm text-muted-foreground flex items-center gap-2', className)}>
      <PercentChangeBadge percent={Math.round(growthPercentage)} />
      {showTimeRange && timeRange && (
        <span className="font-sans text-sm font-normal leading-6 text-gray-500">
          {timeRangePrefix} {timeRange}
        </span>
      )}
    </div>
  );

  const shouldShowTooltip = showComparisonTooltip;

  if (!shouldShowTooltip) {
    return indicator;
  }

  // Wrap the indicator in a tooltip when explicitly enabled and previous data exists
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{indicator}</TooltipTrigger>
        <TooltipContent role="tooltip" aria-live="polite">
          {getPreviousPeriodTooltipText(startDate, endDate, dateType, current, previous, formatType)}
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Convenience component for just the percentage badge without time range
export const MetricPercentageBadge: React.FC<Pick<MetricChangeIndicatorProps, 'current' | 'previous'>> = ({
  current,
  previous,
}) => {
  const growthPercentage = calculatePercentageChange(current, previous);
  return <PercentChangeBadge percent={Math.round(growthPercentage)} />;
};

export default MetricChangeIndicator;
